import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { UserCard } from "@/components/user-card/UserCard.tsx";
import { useApplicantsMentions } from "@/queries/recruit/use-applicants.query.ts";
import { useJobsSearch } from "@/queries/recruit/use-jobs.query.ts";
import {
  ApiJobsFilter,
  ApiJobsFilterDefault,
  getJobEmoji,
  JobStatus,
} from "@/types/recruit/job.types.ts";
import { Avatar, Box, FontAwesomeIcon, Skeleton, Stack, Text, Title } from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export function HomeJobs() {
  const [jobsFilter] = useState<ApiJobsFilter>({
    ...ApiJobsFilterDefault,
    status_list: [JobStatus.published, JobStatus.draft],
    my_jobs: true,
  });
  const { data: mentions } = useApplicantsMentions();
  const hasUnreadMentions = (mentions?.unread_mentions_count ?? 0) > 0;
  const hasAuthors = (mentions?.authors.length ?? 0) > 0;
  const { data: jobs, isLoading: jobsIsLoading } = useJobsSearch(jobsFilter);
  const { t } = useTranslation();

  return (
    <>
      {jobsIsLoading && (
        <Box className={"pt-10"}>
          <Skeleton className={"w-full h-64"} />
        </Box>
      )}

      {((jobs && jobs.items.length > 0) || hasUnreadMentions) && (
        <>
          <Title
            header={t("ats:my_active_jobs")}
            size={"lg"}
            paddingBottom
            paddingTop
            to={"/recruitment/jobs"}
            actionIcon={<FontAwesomeIcon icon={"fa-solid fa-chevron-right"} />}
          />

          <Stack direction={"horizontal"} gap={"md"} className={"flex-wrap"}>
            {hasAuthors && (
              <UserCard
                size={"md"}
                className={"shrink-0"}
                to={`/recruitment/candidates/?mention_state=any`}
                title={hasUnreadMentions ? t("core:new_mentions") : t("core:mentions")}
                captionSlot={
                  hasUnreadMentions ? (
                    <Text className={"px-2 text-new font-semibold block"}>
                      {t("plurals:new_mentions", {
                        count: mentions?.unread_mentions_count ?? 0,
                      })}
                    </Text>
                  ) : (
                    <></>
                  )
                }
                avatarElement={
                  <Box
                    className={
                      "w-41 h-41 p-(--gap-smd) bg-brand-dark/5 rounded-lg aspect-square grid grid-cols-2 gap-sm relative shrink-0"
                    }
                  >
                    {hasUnreadMentions && (
                      <Box
                        className={
                          "w-4 h-4 bg-new rounded-full flex shrink-0 absolute -top-1 -right-1"
                        }
                      ></Box>
                    )}

                    {mentions?.authors.slice(0, 4).map((directEmployee) => (
                      <EmployeeAvatar
                        key={`direct-employee-${directEmployee.id}`}
                        employee={directEmployee}
                        size={"xl"}
                      />
                    ))}
                  </Box>
                }
              />
            )}
            {jobs?.items.slice(0, hasUnreadMentions || hasAuthors ? 4 : 5).map((job) => (
              <UserCard
                size={"md"}
                className={"shrink-0"}
                title={job.title}
                to={`/recruitment/candidates/?job_id=${job.id}`}
                key={job.id}
                captionSlot={
                  <Text
                    className={twMerge(
                      "px-2",
                      job.unread_mention_count > 0 ? "text-new font-semibold" : "text-secondary"
                    )}
                  >
                    {job.unread_mention_count > 0
                      ? t("plurals:new_mentions", { count: job.unread_mention_count })
                      : t("plurals:plural_candidates", {
                          count: job.candidates_count,
                        })}
                  </Text>
                }
                avatarElement={
                  <Box className={"relative"}>
                    {job.unread_mention_count > 0 && (
                      <Box
                        className={
                          "w-4 h-4 bg-new rounded-full flex shrink-0 absolute -top-1 -right-1"
                        }
                      ></Box>
                    )}

                    <Avatar size={"2xl"} emoji={getJobEmoji(job)} />
                  </Box>
                }
              />
            ))}
          </Stack>
        </>
      )}
    </>
  );
}
